import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, TextArea, Badge, Avatar } from '../components/Components';
import { userAPI, miscAPI } from '../utils/api';
import toast from 'react-hot-toast';
import './Profile.css';

export default function EditProfile() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', bio: '', phone: '', location: '',
    expertise: [], coachingInterests: '', rate: 80,
    experience: '', interests: [], goals: '',
  });

  useEffect(() => {
    miscAPI.getExpertise().then(r => setAreas(r.data.areas)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        bio: user.bio || '',
        phone: user.phone || '',
        location: user.location || '',
        expertise: user.expertise || [],
        coachingInterests: user.coachingInterests || '',
        rate: user.rate || 80,
        experience: user.experience || '',
        interests: user.interests || [],
        goals: user.goals || '',
      });
    }
  }, [user]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const toggleArray = (key, val) => {
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
    }));
  };

  const handleSave = async () => {
    if (user.role === 'coach' && form.expertise.length === 0) return toast.error('Select at least one area of expertise');
    if (user.role === 'coach' && !form.coachingInterests) return toast.error('Coaching interests are mandatory');

    setSaving(true);
    try {
      const res = await userAPI.updateProfile(form);
      updateUser(res.data.user);
      toast.success('Profile updated! ✅');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally { setSaving(false); }
  };

  const initials = user?.avatar || user?.name?.split(' ').map(n => n[0]).join('');
  const isCoach = user?.role === 'coach';

  return (
    <div className="page-container" style={{ maxWidth: 640 }}>
      <h2 className="profile-title">Edit Profile</h2>

      <Card className="profile-card">
        <div className="profile-avatar-section">
          <Avatar initials={initials || '?'} size="xl" />
          <div className="profile-avatar-label">{user?.role}</div>
        </div>

        <Input label="Full Name" value={form.name} onChange={e => setField('name', e.target.value)} />

        <div className="profile-row">
          <Input label="Phone" value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="+1 234 567 890" />
          <Input label="Location" value={form.location} onChange={e => setField('location', e.target.value)} placeholder="City, Country" />
        </div>

        <TextArea label="Bio" value={form.bio} onChange={e => setField('bio', e.target.value)} placeholder="Tell people about yourself..." />

        {isCoach && (
          <>
            <div className="input-group">
              <label className="input-label">Areas of Expertise * <span style={{fontWeight:400,color:'#94a3b8'}}>(mandatory)</span></label>
              <div className="profile-badges">{areas.map(a => (
                <Badge key={a} variant="primary" selected={form.expertise.includes(a)} onClick={() => toggleArray('expertise', a)}>{a}</Badge>
              ))}</div>
            </div>

            <TextArea
              label="Coaching Interests & Motivation *"
              value={form.coachingInterests}
              onChange={e => setField('coachingInterests', e.target.value)}
              placeholder="Why are you passionate about coaching?"
            />

            <div className="profile-row">
              <Input label="Hourly Rate ($)" type="number" value={form.rate} onChange={e => setField('rate', Number(e.target.value))} />
              <Input label="Experience" value={form.experience} onChange={e => setField('experience', e.target.value)} placeholder="e.g. 10 years" />
            </div>
          </>
        )}

        {!isCoach && (
          <>
            <div className="input-group">
              <label className="input-label">Areas of Interest</label>
              <div className="profile-badges">{areas.map(a => (
                <Badge key={a} variant="primary" selected={form.interests.includes(a)} onClick={() => toggleArray('interests', a)}>{a}</Badge>
              ))}</div>
            </div>
            <TextArea label="Goals" value={form.goals} onChange={e => setField('goals', e.target.value)} placeholder="What do you want to achieve?" />
          </>
        )}

        <Button fullWidth onClick={handleSave} disabled={saving} style={{ marginTop: 12 }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Card>
    </div>
  );
}
